"use client";
import React from "react";
import Link from "next/link";
import {
  FileText,
  Check,
  AlertCircle,
  Info,
  User,
  Building2,
  CreditCard,
  Shield,
  Scale,
  RefreshCw,
  Receipt,
  Percent,
  Ban,
  Flag,
  Clock,
} from "lucide-react";

const SECTIONS: { id: string; title: string }[] = [
  { id: "descriere-servicii", title: "Descrierea Serviciilor" },
  { id: "acceptare", title: "Acceptarea Termenilor" },
  { id: "cont", title: "Crearea și Gestionarea Contului" },
  { id: "utilizare", title: "Utilizarea Platformei" },
  { id: "plata", title: "Plăți între Sportivi și Proprietari" },
  { id: "abonament", title: "Abonamentul Proprietarilor de Locații" },
  { id: "date", title: "Protecția Datelor cu Caracter Personal" },
  { id: "proprietate", title: "Proprietatea Intelectuală" },
  { id: "continut-ilegal", title: "Raportarea Conținutului Ilegal" },
  { id: "raspundere", title: "Limitarea Răspunderii" },
  { id: "modificare", title: "Modificarea Termenilor" },
  { id: "reziliere", title: "Rezilierea Accesului" },
  { id: "litigii", title: "Legea Aplicabilă și Soluționarea Litigiilor" },
  { id: "contact", title: "Informații de Contact" },
  { id: "final", title: "Dispoziții Finale" },
];

const TermsView = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      <main className="container mx-auto px-4 py-12 pt-24">
        <div className="max-w-4xl mx-auto bg-gray-800/50 rounded-xl border border-gray-700/50 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-gray-700/50">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <FileText className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Termeni și Condiții Atlivo
                </h1>
                <p className="text-gray-300">
                  Versiunea 2.0 · Ultima actualizare: 27 Iulie 2026
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8 text-gray-300">
            <p>
              Bine ați venit pe Atlivo (denumit în continuare „Platforma" sau
              „Atlivo"), o aplicație web operată de SEERSTECH SRL, cu sediul în
              Sălaj, Municipiul Zalău, Strada AVRAM IANCU, Nr. 39, Bloc N7, Ap.
              7, înregistrată la Registrul Comerțului sub nr.
              J31/131/15.02.2023, cod fiscal 47639966, e-mail:
              support@atlivo.com, (denumită în continuare „Operatorul" sau
              „Noi").
            </p>

            <p>
              Acești Termeni și Condiții („Termenii") reglementează utilizarea
              Platformei Atlivo, inclusiv toate funcționalitățile, serviciile și
              conținutul oferit prin intermediul acesteia. Accesul, vizitarea
              sau utilizarea Platformei implică acceptarea explicită a acestor
              Termeni și reprezintă acordul complet dintre dumneavoastră și
              Operator. Dacă nu sunteți de acord cu acești Termeni, vă rugăm să
              nu utilizați Platforma.
            </p>

            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <p>
                  <span className="font-semibold text-white">
                    Ce este nou în versiunea 2.0:
                  </span>{" "}
                  am introdus secțiunea{" "}
                  <a href="#abonament" className="text-blue-400 underline">
                    Abonamentul Proprietarilor de Locații
                  </a>
                  , care descrie complet planul, perioada de probă, reînnoirea
                  automată, facturarea și anularea; am clarificat ce plăți
                  procesăm și ce plăți nu procesăm; și am actualizat secțiunea
                  privind soluționarea litigiilor.
                </p>
              </div>
            </div>

            <div className="bg-gray-700/30 p-6 rounded-xl">
              <h2 className="text-xl font-bold text-white mb-4">Cuprins</h2>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                {SECTIONS.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex gap-2 items-center text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <span className="bg-blue-500/20 text-blue-400 w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    <span>{section.title}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Sections */}
            <section id="descriere-servicii" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                1. Descrierea Serviciilor
              </h2>
              <p className="mb-4">
                Atlivo este o platformă care permite utilizatorilor să:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Caute și rezerve locații sportive (cum ar fi săli de
                    fitness, terenuri de fotbal, baschet etc.);
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Gestioneze rezervări pentru activități sportive;</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Administreze locații sportive, terenuri, program, prețuri,
                    personal și rapoarte (pentru proprietarii de locații, pe
                    bază de abonament);
                  </span>
                </li>
              </ul>

              <div className="mt-6 p-4 bg-gray-700/30 rounded-lg">
                <p className="mb-2">Serviciile sunt disponibile pentru:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div className="flex items-start gap-3 bg-gray-800/70 p-4 rounded-lg">
                    <User className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">Sportivi</h3>
                      <p className="text-sm">
                        Persoane care doresc să participe la activități
                        sportive. Accesul este gratuit.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-gray-800/70 p-4 rounded-lg">
                    <Building2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">
                        Proprietari de locații
                      </h3>
                      <p className="text-sm">
                        Persoane sau entități care oferă spații pentru
                        activități sportive, în cadrul activității lor
                        profesionale. Accesul se face pe bază de abonament.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="acceptare" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                2. Acceptarea Termenilor
              </h2>
              <p className="mb-4">
                Prin accesarea sau utilizarea Platformei, confirmați că:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Aveți cel puțin 18 ani sau vârsta legală pentru a încheia
                    contracte în jurisdicția dumneavoastră;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Ați citit, înțeles și acceptat acești Termeni și Politica de
                    Confidențialitate;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Vă angajați să respectați toate legile și reglementările
                    aplicabile.
                  </span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-yellow-300">
                    Utilizarea Platformei fără acceptarea Termenilor este
                    interzisă.
                  </p>
                </div>
              </div>
            </section>

            <section id="cont" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                3. Crearea și Gestionarea Contului
              </h2>

              <div className="space-y-6">
                <div className="pl-4 border-l-2 border-purple-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    3.1. Înregistrarea Contului
                  </h3>
                  <p className="mb-3">
                    Pentru a utiliza anumite funcționalități (cum ar fi
                    rezervările), trebuie să vă creați un cont. În timpul
                    procesului de înregistrare, vi se va solicita să furnizați
                    informații corecte și complete, inclusiv:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                      <span>
                        Prenume și nume (opțional, se poate folosi și un
                        nickname);
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                      <span>Adresă de e-mail;</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                      <span>
                        Număr de telefon (opțional, dar necesar pentru
                        rezervări);
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                      <span>Rolul: sportiv sau proprietar de locație.</span>
                    </li>
                  </ul>
                  <p className="mt-3">
                    Sunteți responsabil pentru menținerea confidențialității
                    datelor de autentificare și pentru toate activitățile
                    efectuate prin contul dumneavoastră.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-purple-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    3.2. Verificarea Contului și a Locației
                  </h3>
                  <p>
                    Putem solicita verificarea identității sau a numărului de
                    telefon pentru a preveni frauda și a asigura securitatea
                    Platformei. Locațiile sportive sunt supuse unui proces de
                    verificare înainte de a deveni vizibile public; până la
                    verificare, pagina locației este accesibilă doar
                    proprietarului și personalului acestuia.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-purple-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    3.3. Conturi de Personal
                  </h3>
                  <p>
                    Proprietarul unei locații poate invita membri ai
                    personalului, cărora le poate acorda drepturi de
                    administrare. Proprietarul răspunde pentru acțiunile
                    efectuate de personalul său prin Platformă și pentru
                    corectitudinea drepturilor acordate. Acțiunile efectuate
                    asupra rezervărilor sunt înregistrate într-un jurnal de
                    activitate, împreună cu numele persoanei care le-a efectuat.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-purple-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    3.4. Suspendarea sau Ștergerea Contului
                  </h3>
                  <p className="mb-3">
                    Ne rezervăm dreptul de a suspenda sau șterge contul
                    dumneavoastră dacă:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                      <span>Furnizați informații false sau incomplete;</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                      <span>Încălcați acești Termeni;</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                      <span>Utilizați Platforma în mod abuziv sau ilegal.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="utilizare" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                4. Utilizarea Platformei
              </h2>

              <div className="space-y-6">
                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    4.1. Comportamentul Utilizatorilor
                  </h3>
                  <p className="mb-3">
                    Vă angajați să utilizați Platforma în mod legal și
                    responsabil. Este interzis să:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Publicați conținut ofensator, defăimător, ilegal sau
                        care încalcă drepturile altor persoane;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Încărcați fișiere care conțin viruși sau software
                        dăunător;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Încălcați drepturile de proprietate intelectuală ale
                        Operatorului sau ale terților;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Utilizați Platforma pentru a hărțui, amenința sau
                        discrimina alți utilizatori;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Încercați să accesați neautorizat sistemele sau datele
                        Platformei.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    4.2. Rezervările
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-gray-800/70 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-400 mb-2">
                        Pentru Sportivi:
                      </h4>
                      <p>
                        Puteți rezerva locații sportive prin Platformă,
                        respectând programul și condițiile stabilite de
                        proprietarii locațiilor.
                      </p>
                    </div>
                    <div className="bg-gray-800/70 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-400 mb-2">
                        Pentru Proprietari de Locații:
                      </h4>
                      <p>
                        Sunteți responsabil pentru actualizarea informațiilor
                        despre locație (program, prețuri, disponibilitate) și
                        pentru onorarea rezervărilor confirmate.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    4.3. Anularea Rezervărilor
                  </h3>
                  <p>
                    Politicile de anulare sunt stabilite de fiecare proprietar
                    de locație și vor fi afișate clar înainte de rezervare. Vă
                    rugăm să verificați aceste politici înainte de a efectua o
                    rezervare.
                  </p>
                </div>
              </div>
            </section>

            <section id="plata" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                5. Plăți între Sportivi și Proprietari
              </h2>

              <div className="space-y-6">
                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    5.1. Utilizarea Platformei de către Sportivi
                  </h3>
                  <div className="flex items-start gap-3 p-4 bg-gray-800/70 rounded-lg">
                    <CreditCard className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <p>
                      Platforma Atlivo este{" "}
                      <span className="font-semibold text-green-400">
                        gratuită pentru sportivi
                      </span>
                      : crearea contului, căutarea locațiilor și efectuarea
                      rezervărilor nu presupun niciun cost. Costurile se aplică
                      exclusiv proprietarilor de locații, sub forma
                      abonamentului descris la{" "}
                      <a href="#abonament" className="text-blue-400 underline">
                        secțiunea 6
                      </a>
                      .
                    </p>
                  </div>
                </div>

                <div className="pl-4 border-l-2 border-orange-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    5.2. Plata Utilizării Locațiilor Sportive
                  </h3>
                  <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <p className="text-orange-300 font-medium">
                        Atlivo nu procesează și nu intermediază plățile dintre
                        sportivi și proprietarii de locații. Pentru aceste
                        tranzacții, Platforma funcționează exclusiv ca
                        intermediar de rezervări.
                      </p>
                    </div>
                  </div>
                  <p className="mb-4">
                    Plata pentru utilizarea unei locații sportive rezervate prin
                    intermediul Platformei se efectuează exclusiv la fața
                    locului, direct către proprietarul locației, conform
                    condițiilor și termenilor stabiliți de acesta. Atlivo nu
                    face parte din această relație financiară.
                  </p>
                  <div className="p-4 bg-gray-800/70 rounded-lg mb-4">
                    <p className="text-sm">
                      <span className="font-semibold text-white">
                        Notă privind abonamentul:
                      </span>{" "}
                      această regulă privește exclusiv relația dintre sportivi
                      și proprietari. Abonamentul lunar datorat de proprietarii
                      de locații către Atlivo{" "}
                      <span className="font-semibold text-white">este</span>{" "}
                      încasat de noi, prin procesatorul de plăți Stripe, în
                      condițiile de la{" "}
                      <a href="#abonament" className="text-blue-400 underline">
                        secțiunea 6
                      </a>
                      .
                    </p>
                  </div>
                  <p className="mb-3 font-medium text-white">
                    Atlivo nu este responsabil pentru:
                  </p>
                  <ul className="space-y-2 ml-4 mb-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Diferențe între prețul afișat pe Platformă și prețul
                        solicitat efectiv la locație;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Refuzul proprietarului de a onora prețul sau condițiile
                        publicate pe Platformă;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Litigii financiare apărute între sportivi și
                        proprietarii de locații;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Rambursări aferente sumelor achitate direct
                        proprietarilor de locații — acestea nu pot fi procesate
                        prin Atlivo.
                      </span>
                    </li>
                  </ul>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium mb-2">
                          Angajamentul Atlivo față de corectitudinea
                          informațiilor
                        </p>
                        <p className="mb-3">
                          Deși nu putem interveni în relația financiară directă
                          dintre utilizatori și proprietarii de locații, ne
                          angajăm să menținem integritatea datelor afișate pe
                          Platformă. În cazul în care un proprietar de locație
                          nu respectă prețurile sau condițiile publicate,
                          utilizatorii pot raporta situația la{" "}
                          <span className="text-blue-400">
                            support@atlivo.com
                          </span>
                          . Atlivo va:
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Notifica și avertiza formal proprietarul de
                              locație în cauză cu privire la neconformitate;
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                            <span>
                              În cazul încălcărilor repetate sau al refuzului de
                              a remedia situația, suspenda temporar sau elimina
                              permanent contul proprietarului de pe Platformă,
                              pentru a proteja comunitatea de utilizatori.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="abonament" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                6. Abonamentul Proprietarilor de Locații
              </h2>
              <p className="mb-6">
                Această secțiune se aplică exclusiv proprietarilor de locații
                sportive care activează un abonament Atlivo. Abonamentul se
                încheie per locație: fiecare locație administrată are propriul
                abonament, propriile date de facturare și propriul ciclu de
                facturare.
              </p>

              <div className="space-y-6">
                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.1. Planul și Prețul
                  </h3>
                  <p className="mb-3">
                    Abonamentul standard oferă acces la toate funcționalitățile
                    de administrare a locației, contra unei sume lunare fixe.
                    Atlivo{" "}
                    <span className="font-semibold text-white">
                      nu percepe comision
                    </span>{" "}
                    din rezervările efectuate prin Platformă.
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Prețul aplicabil este cel afișat pe pagina de abonament
                        a locației dumneavoastră în momentul activării, precum
                        și în e-mailul de confirmare și pe factură. Prețul poate
                        include o reducere promoțională sau negociată
                        individual;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Prețurile sunt exprimate în{" "}
                        <span className="font-semibold text-white">
                          lei (RON)
                        </span>{" "}
                        și{" "}
                        <span className="font-semibold text-white">
                          nu includ TVA
                        </span>
                        , dacă nu se indică altfel. TVA-ul aplicabil se adaugă
                        la momentul facturării, conform secțiunii 6.7;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Ciclul de facturare este lunar și începe la data primei
                        plăți reușite;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Dacă v-a fost acordat un preț promoțional garantat pe
                        durata abonamentului (de exemplu, în cadrul unei oferte
                        de lansare), acesta rămâne valabil pe toată durata
                        neîntreruptă a abonamentului dumneavoastră, în
                        condițiile secțiunii 6.8.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.2. Perioada de Probă Gratuită
                  </h3>
                  <div className="flex items-start gap-3 p-4 bg-gray-800/70 rounded-lg mb-3">
                    <Clock className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <p>
                      Abonamentul începe cu o perioadă de probă gratuită de{" "}
                      <span className="font-semibold text-white">
                        60 de zile
                      </span>{" "}
                      (sau durata indicată în invitația dumneavoastră, dacă
                      diferă).{" "}
                      <span className="font-semibold text-white">
                        Nu este necesar un card pentru a începe
                      </span>{" "}
                      și nu se percepe nicio sumă în această perioadă.
                    </p>
                  </div>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Perioada de probă începe în momentul creării contului de
                        proprietar, respectiv al acceptării invitației;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Acordarea perioadei de probă nu creează nicio obligație
                        de plată. Nicio sumă nu poate fi încasată decât după ce
                        dumneavoastră adăugați în mod explicit un card,
                        acceptând reînnoirea automată;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Dacă la finalul perioadei de probă nu există un card
                        salvat, abonamentul expiră automat, fără nicio plată și
                        fără notificare prealabilă de plată. Puteți relua
                        oricând abonamentul din pagina de abonament a locației;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Dacă adăugați un card în timpul perioadei de probă,
                        păstrați zilele rămase din aceasta; prima plată are loc
                        abia la finalul perioadei de probă.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.3. Datele de Facturare
                  </h3>
                  <p className="mb-3">
                    Înainte de activarea unui abonament plătit, trebuie să
                    completați datele de facturare ale locației: denumirea
                    legală, codul fiscal (CUI pentru persoane juridice; CNP-ul
                    este opțional pentru persoane fizice), numărul de la
                    Registrul Comerțului, adresa completă și adresa de e-mail
                    pentru facturi. Fără aceste date nu putem emite o factură
                    conformă și abonamentul nu poate fi pornit.
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Sunteți responsabil pentru corectitudinea și
                        actualizarea acestor date. Facturile emise pe baza unor
                        date incorecte furnizate de dumneavoastră nu pot fi
                        stornate din acest motiv;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Pentru comoditate, putem prelua automat datele firmei
                        din registrul public al ANAF pe baza CUI-ului
                        introdus. Această preluare este o facilitate de
                        completare, nu o verificare din partea noastră; datele
                        rămân în responsabilitatea dumneavoastră;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Datele de facturare valabile la momentul fiecărei plăți
                        sunt înregistrate împreună cu plata respectivă, astfel
                        încât factura emisă să rămână corectă chiar dacă ulterior
                        vă modificați datele.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.4. Metoda de Plată și Reînnoirea Automată
                  </h3>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-3">
                    <div className="flex items-start gap-3">
                      <RefreshCw className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <p className="text-yellow-200">
                        Abonamentul se{" "}
                        <span className="font-semibold text-white">
                          reînnoiește automat
                        </span>{" "}
                        din lună în lună, prin debitarea cardului salvat, până
                        când îl anulați. Prin adăugarea unui card vă exprimați
                        acordul pentru aceste plăți recurente.
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Plățile sunt procesate de{" "}
                        <span className="font-semibold text-white">Stripe</span>
                        . Datele complete ale cardului sunt introduse direct în
                        pagina securizată a procesatorului și{" "}
                        <span className="font-semibold text-white">
                          nu sunt stocate niciodată de Atlivo
                        </span>
                        ; noi păstrăm doar o referință și informații
                        neidentificatoare (tipul cardului, ultimele cifre, data
                        expirării);
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        La finalul fiecărei perioade, cardul salvat este debitat
                        automat cu suma datorată (preț + TVA), fără o
                        confirmare suplimentară din partea dumneavoastră;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        În anumite cazuri, banca dumneavoastră poate solicita o
                        autentificare suplimentară (3-D Secure). Dacă
                        autentificarea nu poate fi finalizată, plata este
                        considerată nereușită, conform secțiunii 6.5;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Puteți schimba sau elimina oricând cardul salvat din
                        pagina de abonament. Eliminarea cardului nu anulează
                        abonamentul: dacă nu adăugați un card nou, următoarea
                        reînnoire va eșua.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-red-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.5. Plata Nereușită
                  </h3>
                  <p className="mb-3">
                    Dacă o plată nu poate fi procesată (fonduri insuficiente,
                    card expirat, refuz al băncii, autentificare nefinalizată),
                    abonamentul intră în starea{" "}
                    <span className="font-semibold text-white">
                      „Plată necesară"
                    </span>{" "}
                    și:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Vă notificăm în aplicație pentru a actualiza metoda de
                        plată;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Beneficiați de o perioadă de grație de{" "}
                        <span className="font-semibold text-white">
                          14 zile
                        </span>{" "}
                        de la finalul perioadei plătite, în care accesul la
                        funcționalitățile de administrare este menținut;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        După expirarea perioadei de grație, accesul la
                        funcționalitățile de administrare poate fi restricționat
                        și locația poate fi retrasă din listările publice, până
                        la reluarea plății;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Restricționarea accesului nu vă exonerează de obligația
                        de a onora rezervările deja confirmate față de sportivi.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-green-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.6. Anularea, Reactivarea și Rambursările
                  </h3>
                  <p className="mb-3">
                    Puteți anula abonamentul{" "}
                    <span className="font-semibold text-white">oricând</span>,
                    fără penalități, direct din pagina de abonament a locației
                    (butonul „Anulează abonamentul") sau prin e-mail la
                    support@atlivo.com.
                  </p>
                  <ul className="space-y-2 ml-4 mb-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Anularea produce efecte{" "}
                        <span className="font-semibold text-white">
                          la finalul perioadei deja plătite
                        </span>{" "}
                        (sau al perioadei de probă). Până atunci păstrați accesul
                        complet, iar abonamentul nu se mai reînnoiește;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Vă puteți răzgândi: până la finalul perioadei curente
                        puteți reactiva abonamentul, păstrând cardul și perioada
                        de facturare existente. Reactivarea nu generează o plată
                        imediată;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        După încheierea abonamentului puteți porni oricând unul
                        nou, în condițiile aplicabile la acel moment.
                      </span>
                    </li>
                  </ul>
                  <div className="p-4 bg-gray-800/70 rounded-lg">
                    <p className="mb-2 font-medium text-white">
                      Politica de rambursare
                    </p>
                    <p>
                      Întrucât accesul rămâne activ pe toată perioada plătită,{" "}
                      <span className="font-semibold text-white">
                        sumele aferente perioadei în curs nu se rambursează
                        proporțional
                      </span>{" "}
                      în caz de anulare. Rambursăm integral, în termen de maximum
                      14 zile de la constatare, sumele încasate din eroare (de
                      exemplu, o dublă debitare sau o plată efectuată după
                      anulare), precum și în cazurile în care rambursarea este
                      obligatorie potrivit legii. Cererile se trimit la
                      support@atlivo.com.
                    </p>
                  </div>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.7. Facturarea și TVA
                  </h3>
                  <div className="flex items-start gap-3 p-4 bg-gray-800/70 rounded-lg mb-3">
                    <Receipt className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p>
                      Pentru fiecare plată reușită emitem o factură în format
                      electronic, pe care o transmitem la adresa de e-mail pentru
                      facturi indicată în datele de facturare. Facturile sunt
                      disponibile și în pagina de abonament a locației.
                    </p>
                  </div>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <Percent className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                      <span>
                        Pentru clienții din România se aplică cota standard de
                        TVA în vigoare (în prezent{" "}
                        <span className="font-semibold text-white">21%</span>).
                        Cota afișată în aplicație are caracter informativ; cota
                        și suma finale sunt cele calculate la momentul emiterii
                        facturii;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Percent className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                      <span>
                        Pentru clienții persoane impozabile înregistrate în
                        scopuri de TVA în alt stat membru UE se poate aplica
                        mecanismul de taxare inversă. În acest caz sunteți
                        responsabil pentru furnizarea unui cod valid de TVA și
                        pentru declararea TVA-ului în statul dumneavoastră;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Percent className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                      <span>
                        Facturile vor fi raportate în sistemul național RO
                        e-Factura, în condițiile și la termenele prevăzute de
                        lege.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-blue-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.8. Modificarea Prețului
                  </h3>
                  <p className="mb-3">
                    Putem modifica prețul abonamentului, cu respectarea
                    următoarelor reguli:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Vă anunțăm prin e-mail cu cel puțin{" "}
                        <span className="font-semibold text-white">
                          30 de zile
                        </span>{" "}
                        înainte ca noul preț să se aplice;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Puteți anula abonamentul înainte de intrarea în vigoare a
                        noului preț, fără costuri suplimentare. Continuarea
                        abonamentului după această dată înseamnă acceptarea
                        noului preț;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Prețurile promoționale garantate pe durata abonamentului
                        nu sunt afectate de majorările de preț, atât timp cât
                        abonamentul rămâne neîntrerupt pentru locația
                        respectivă. Dacă abonamentul este anulat sau expiră,
                        prețul garantat se pierde, iar un abonament nou se
                        activează la prețul curent;
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>
                        Modificările cotelor de TVA sau ale altor taxe impuse
                        prin lege se aplică de drept, de la data intrării lor în
                        vigoare, și nu constituie o modificare de preț în sensul
                        acestei secțiuni.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-purple-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.9. Planuri Personalizate și Plata prin Transfer Bancar
                  </h3>
                  <p>
                    Pentru anumiți clienți putem conveni planuri personalizate,
                    reduceri, durate diferite ale perioadei de probă sau plata
                    prin transfer bancar (ordin de plată), pe baza facturii
                    emise. Condițiile agreate individual, în scris, prevalează
                    asupra prevederilor standard din această secțiune. În lipsa
                    unei mențiuni contrare, restul Termenilor rămâne aplicabil.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-orange-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    6.10. Statutul de Profesionist și Dreptul de Retragere
                  </h3>
                  <p className="mb-3">
                    Abonamentul Atlivo este destinat utilizării în scop
                    profesional, de către persoane care oferă spre închiriere
                    locații sportive în cadrul activității lor economice. În
                    aceste situații, abonatul nu are calitatea de consumator, iar
                    dispozițiile privind dreptul de retragere din contractele la
                    distanță încheiate cu consumatorii nu sunt aplicabile.
                  </p>
                  <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium mb-2">
                          Dacă totuși aveți calitatea de consumator
                        </p>
                        <p className="mb-3">
                          Dacă, prin excepție, contractați abonamentul în afara
                          activității dumneavoastră comerciale sau profesionale,
                          beneficiați de dreptul de a vă retrage din contract în
                          termen de{" "}
                          <span className="font-semibold text-white">
                            14 zile
                          </span>{" "}
                          de la încheierea acestuia, fără a fi nevoie să
                          justificați decizia și fără penalități.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Vă puteți exercita acest drept folosind funcția
                              „Anulează abonamentul" din pagina de abonament a
                              locației, disponibilă permanent în contul
                              dumneavoastră, sau printr-o cerere transmisă la
                              support@atlivo.com;
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Vă confirmăm primirea cererii, fără întârziere
                              nejustificată, pe un suport durabil (e-mail), cu
                              precizarea momentului înregistrării acesteia;
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <span>
                              Întrucât abonamentul începe cu o perioadă de probă
                              gratuită, iar prima plată are loc abia după
                              încheierea acesteia, în mod obișnuit nu există sume
                              de rambursat. Dacă totuși s-a efectuat o plată,
                              aceasta se restituie în termen de cel mult 14 zile
                              de la primirea cererii, prin aceeași metodă de
                              plată, cu reținerea contravalorii serviciilor
                              prestate până la retragere, dacă ați solicitat
                              expres începerea prestării în perioada de
                              retragere.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="date" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                7. Protecția Datelor cu Caracter Personal
              </h2>
              <div className="flex items-start gap-3 p-5 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4">
                <Shield className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="mb-2">
                    Procesăm datele dumneavoastră personale în conformitate cu
                    Regulamentul (UE) 2016/679 (GDPR) și legislația românească
                    aplicabilă.
                  </p>
                  <Link
                    href="/docs/privacy"
                    className="text-blue-400 hover:text-blue-300 transition-colors underline"
                  >
                    Consultați Politica de Confidențialitate pentru detalii
                    complete.
                  </Link>
                </div>
              </div>

              <div className="pl-4 border-l-2 border-blue-500/50">
                <h3 className="text-lg font-semibold text-white mb-2">
                  7.1. Datele Sportivilor Primite de Proprietarii de Locații
                </h3>
                <p className="mb-3">
                  Pentru a putea onora rezervările, proprietarii de locații
                  primesc date cu caracter personal ale sportivilor (nume, date
                  de contact, detalii ale rezervării). În raport cu aceste date,
                  proprietarul de locație acționează ca{" "}
                  <span className="font-semibold text-white">
                    operator independent
                  </span>
                  , nu ca împuternicit al Atlivo. În consecință, proprietarul de
                  locație:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>
                      Poate utiliza aceste date exclusiv pentru gestionarea și
                      onorarea rezervărilor și pentru comunicările necesare în
                      acest scop;
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>
                      Nu poate utiliza datele în scopuri de marketing fără un
                      temei legal propriu și nu le poate transmite terților
                      neautorizați;
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>
                      Răspunde de respectarea propriilor obligații GDPR,
                      inclusiv de informarea persoanelor vizate, de securitatea
                      datelor și de soluționarea cererilor privind drepturile
                      acestora;
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>
                      Răspunde de accesul acordat propriului personal prin
                      conturile create pe Platformă.
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            <section id="proprietate" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                8. Proprietatea Intelectuală
              </h2>
              <p className="mb-4">
                Toate conținuturile Platformei (text, imagini, logo-uri,
                software, design) sunt proprietatea Operatorului sau a
                partenerilor săi și sunt protejate de legile privind
                proprietatea intelectuală. Este interzisă copierea, modificarea,
                distribuirea sau utilizarea neautorizată a acestor conținuturi.
              </p>
              <p>
                Utilizatorii care încarcă conținut (ex. fotografii ale
                locațiilor) acordă Operatorului o licență neexclusivă, gratuită,
                pentru a utiliza acest conținut în scopul funcționării
                Platformei.
              </p>
            </section>

            <section id="continut-ilegal" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                9. Raportarea Conținutului Ilegal
              </h2>
              <div className="flex items-start gap-3 p-5 bg-gray-800/70 rounded-lg">
                <Flag className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-3">
                  <p>
                    Orice persoană sau entitate ne poate semnala conținut
                    considerat ilegal, publicat pe Platformă, prin e-mail la{" "}
                    <span className="text-blue-400">support@atlivo.com</span>.
                    Pentru a putea analiza sesizarea, vă rugăm să includeți: o
                    explicație a motivelor pentru care considerați conținutul
                    ilegal, adresa (URL-ul) exactă la care se află, numele și
                    adresa dumneavoastră de e-mail și o declarație privind buna
                    dumneavoastră credință cu privire la acuratețea sesizării.
                  </p>
                  <p>
                    Confirmăm primirea sesizării și comunicăm decizia luată,
                    împreună cu motivele acesteia, atât persoanei care a făcut
                    sesizarea, cât și utilizatorului afectat de măsura luată.
                    Măsurile pot include eliminarea conținutului, restricționarea
                    vizibilității acestuia sau suspendarea contului. Deciziile
                    noastre pot fi contestate prin răspuns la aceeași adresă de
                    e-mail.
                  </p>
                  <p>
                    Aceeași adresă de e-mail reprezintă punctul de contact pentru
                    autorități și pentru utilizatori în legătură cu aceste
                    aspecte.
                  </p>
                </div>
              </div>
            </section>

            <section id="raspundere" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                10. Limitarea Răspunderii
              </h2>

              <div className="space-y-6">
                <div className="pl-4 border-l-2 border-yellow-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    10.1. Disponibilitatea Platformei
                  </h3>
                  <p>
                    Operatorul depune toate eforturile pentru a asigura
                    funcționarea continuă a Platformei, dar nu garantează că
                    aceasta va fi lipsită de erori sau disponibilă permanent.
                    Putem efectua lucrări de mentenanță planificată, pe care le
                    anunțăm în prealabil ori de câte ori este posibil.
                  </p>
                </div>

                <div className="pl-4 border-l-2 border-yellow-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    10.2. Răspunderea pentru Rezervări
                  </h3>
                  <p className="mb-3">
                    Operatorul facilitează rezervările, dar nu este responsabil
                    pentru:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                      <span>
                        Calitatea sau disponibilitatea locațiilor sportive;
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                      <span>
                        Anularea sau modificarea rezervărilor de către
                        proprietarii de locații;
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
                      <span>Comportamentul utilizatorilor pe Platformă.</span>
                    </li>
                  </ul>
                </div>

                <div className="pl-4 border-l-2 border-yellow-500/50">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    10.3. Limitarea Daunelor
                  </h3>
                  <p>
                    În măsura permisă de lege, Operatorul nu va fi responsabil
                    pentru daune indirecte, accidentale sau consecvente
                    rezultate din utilizarea Platformei. Pentru abonații
                    profesioniști, răspunderea totală a Operatorului aferentă
                    serviciilor de abonament este limitată la suma efectiv
                    achitată de aceștia în ultimele 12 luni anterioare
                    evenimentului care a generat prejudiciul. Aceste limitări nu
                    se aplică în caz de dol, culpă gravă sau în situațiile în
                    care legea nu permite limitarea răspunderii.
                  </p>
                </div>
              </div>
            </section>

            <section id="modificare" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                11. Modificarea Termenilor
              </h2>
              <p className="mb-4">
                Ne rezervăm dreptul de a actualiza acești Termeni. Modificările
                vor fi comunicate prin:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>Notificare pe Platformă;</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>E-mail către utilizatorii înregistrați.</span>
                </li>
              </ul>
              <p className="mt-4">
                Modificările substanțiale care privesc abonații plătitori sunt
                anunțate cu cel puțin 30 de zile înainte de intrarea lor în
                vigoare, perioadă în care abonamentul poate fi anulat fără
                costuri suplimentare. Modificările de preț sunt supuse regulilor
                de la secțiunea 6.8. Continuarea utilizării Platformei după
                intrarea în vigoare a modificărilor implică acceptarea noilor
                condiții. Fiecare versiune a Termenilor poartă un număr de
                versiune și o dată a ultimei actualizări; versiunile anterioare
                sunt disponibile la cerere, la support@atlivo.com.
              </p>
            </section>

            <section id="reziliere" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                12. Rezilierea Accesului
              </h2>
              <p className="mb-4">
                Putem restricționa sau înceta accesul dumneavoastră la Platformă
                dacă:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Ban className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>Încălcați acești Termeni;</span>
                </li>
                <li className="flex items-start gap-2">
                  <Ban className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>Utilizați Platforma în mod fraudulos sau ilegal;</span>
                </li>
                <li className="flex items-start gap-2">
                  <Ban className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Nu achitați sumele datorate în condițiile secțiunii 6.5;
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Ban className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Este necesar pentru a proteja securitatea Platformei sau a
                    altor utilizatori.
                  </span>
                </li>
              </ul>
            </section>

            <section id="litigii" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                13. Legea Aplicabilă și Soluționarea Litigiilor
              </h2>
              <div className="flex items-start gap-3 p-5 bg-gray-800/70 rounded-lg">
                <Scale className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-3">
                  <p>
                    Acești Termeni sunt guvernați de legislația României. Vă
                    rugăm să ne contactați mai întâi la support@atlivo.com: ne
                    angajăm să încercăm soluționarea amiabilă a oricărei
                    reclamații în termen de 30 de zile de la primirea acesteia.
                  </p>
                  <p>
                    Dacă sunteți consumator, vă puteți adresa Autorității
                    Naționale pentru Protecția Consumatorilor (ANPC),{" "}
                    <a
                      href="https://anpc.ro"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors underline"
                    >
                      anpc.ro
                    </a>
                    , și puteți apela la procedura de soluționare alternativă a
                    litigiilor (SAL), disponibilă la{" "}
                    <a
                      href="https://anpc.ro/ce-este-sal/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors underline"
                    >
                      anpc.ro/ce-este-sal
                    </a>
                    .
                  </p>
                  <p>
                    În lipsa unei soluții amiabile, litigiile vor fi supuse
                    instanțelor competente din România. Pentru raporturile cu
                    abonații profesioniști, competența revine instanțelor de la
                    sediul Operatorului, din Zalău.
                  </p>
                </div>
              </div>
            </section>

            <section id="contact" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                14. Informații de Contact
              </h2>
              <div className="bg-gray-700/30 p-6 rounded-xl">
                <p className="mb-4">
                  Pentru întrebări, reclamații sau exercitarea drepturilor
                  dumneavoastră, ne puteți contacta la:
                </p>
                <ul className="space-y-2">
                  <li>
                    <span className="font-semibold text-white">Denumire:</span>{" "}
                    SEERSTECH SRL
                  </li>
                  <li>
                    <span className="font-semibold text-white">Sediu:</span>{" "}
                    Județul Sălaj, Municipiul Zalău, Strada Avram Iancu, Nr. 39,
                    Bloc N7, Ap. 7
                  </li>
                  <li>
                    <span className="font-semibold text-white">
                      Nr. Reg. Com.:
                    </span>{" "}
                    J31/131/15.02.2023
                  </li>
                  <li>
                    <span className="font-semibold text-white">Cod fiscal:</span>{" "}
                    47639966
                  </li>
                  <li>
                    <span className="font-semibold text-white">E-mail:</span>{" "}
                    support@atlivo.com
                  </li>
                  {/* TODO(legal): ANPC solicită afișarea unui număr de telefon de contact
                      pentru comercianții online (Legea 365/2002). Completați linia de mai jos
                      cu numărul de telefon al societății înainte de publicare.
                  <li>
                    <span className="font-semibold text-white">Telefon:</span>{" "}
                    +40 ...
                  </li>
                  */}
                </ul>
              </div>
            </section>

            <section id="final" className="pt-4">
              <h2 className="text-2xl font-bold text-white mb-4">
                15. Dispoziții Finale
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Acești Termeni, împreună cu Politica de Confidențialitate și
                    cu eventualele condiții agreate individual în scris,
                    reprezintă acordul complet între dumneavoastră și Operator.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Dacă o prevedere este considerată invalidă, celelalte
                    prevederi rămân în vigoare.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Nu puteți cesiona drepturile și obligațiile din acești
                    Termeni fără acordul nostru scris. Operatorul poate cesiona
                    contractul în cadrul unei reorganizări sau transfer de
                    activitate, cu informarea prealabilă a utilizatorilor.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Termenii sunt disponibili în limba română, iar versiunea în
                    limba română prevalează în caz de traducere.
                  </span>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsView;
